import { useEffect, useRef, useState, type CSSProperties } from "react";
import { T } from "@v0y4ge/jrni-ui";

/**
 * NumberField — the STANDARD way to enter numbers across this app.
 *
 * Why not <input type="number">: the native control shows spinner arrows and
 * changes value on mouse-wheel / trackpad scroll, which is fiddly and easy to
 * mis-edit. This renders a plain text input with a numeric software keyboard
 * (inputMode) and sanitizes keystrokes to digits (plus one dot when decimal).
 *
 * Behaviour:
 *   • Emits a parsed number via onChange (empty → 0, clamped to >= min).
 *   • Keeps a local string so a field can be briefly empty / mid-typed
 *     ("10." while typing a decimal) without snapping.
 *   • Syncs when the external numeric value changes from elsewhere.
 *
 * Use this instead of <input type="number"> everywhere a number is entered.
 */
export function NumberField({
  value, onChange, decimal = false, min = 0, placeholder, disabled, style, ariaLabel,
}: {
  value: number;
  onChange: (n: number) => void;
  decimal?: boolean;
  min?: number;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
  ariaLabel?: string;
}) {
  const [str, setStr] = useState(() => (value === 0 ? "0" : String(value)));
  const lastEmitted = useRef(value);

  // Reflect external programmatic changes (not our own emits).
  useEffect(() => {
    if (value !== lastEmitted.current) {
      setStr(value === 0 ? "0" : String(value));
      lastEmitted.current = value;
    }
  }, [value]);

  const parse = (s: string) => {
    const n = decimal ? parseFloat(s) : parseInt(s, 10);
    return Number.isNaN(n) ? 0 : Math.max(min, n);
  };
  const handle = (raw: string) => {
    let cleaned = decimal ? raw.replace(/[^0-9.]/g, "") : raw.replace(/[^0-9]/g, "");
    if (decimal) { const i = cleaned.indexOf("."); if (i >= 0) cleaned = cleaned.slice(0, i + 1) + cleaned.slice(i + 1).replace(/\./g, ""); }
    setStr(cleaned);
    const n = parse(cleaned);
    lastEmitted.current = n;
    onChange(n);
  };
  const onBlur = () => { const n = parse(str); setStr(n === 0 ? "0" : String(n)); };

  const base: CSSProperties = { height: 34, padding: "0 10px", border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 14, fontFamily: T.font, color: T.n1, boxSizing: "border-box", outline: "none", background: disabled ? T.n6 : T.white };

  return (
    <input
      type="text"
      inputMode={decimal ? "decimal" : "numeric"}
      autoComplete="off"
      aria-label={ariaLabel}
      value={str}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => handle(e.target.value)}
      onBlur={onBlur}
      style={{ ...base, ...style }}
    />
  );
}
